import {
  FormControl,
  FormControlProps,
  InputBaseComponentProps,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { forwardRef } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { PatternFormat, PatternFormatProps } from 'react-number-format';
import { PHONE_FIELD_PLACEHOLDER, US_PHONE_CODE } from 'src/components/Forms/const';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { useWatchConditions } from 'src/components/Forms/use-validations';

export type FormPhoneInputProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'name' | 'textFieldProps'
> & {
  label: string;
  helperText?: string;
  required?: boolean | string;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  textFieldProps?: Partial<TextFieldProps>;
  value?: string;
  debounced?: number;
} & CommonFormFieldProps<'phone', string>;

interface RenderPhoneInputProps {
  field: ControllerRenderProps<FieldValues, string>;
  textFieldProps?: Partial<TextFieldProps>;
  variant: TextFieldProps['variant'];
  label: string;
  helperText?: string;
  error: boolean;
  value?: string;
  debounced?: number;
}

export const TextMaskPhone = forwardRef<
  PatternFormatProps,
  Omit<InputBaseComponentProps, 'onChange'> & {
    onChange: (event: { target: { value: string } }) => void;
  }
>(function NumericFormatCustom(props, ref) {
  const { onChange, ...rest } = props;

  return (
    <PatternFormat
      {...rest}
      getInputRef={ref}
      onValueChange={(values) => {
        (onChange as (param: { target: unknown }) => void)?.({
          target: { value: values.value },
        });
      }}
      format={`${US_PHONE_CODE} (###) ### ####`}
    />
  );
});

const RenderPhoneInput = ({
  field,
  textFieldProps,
  label,
  error,
  variant,
  value,
  helperText,
}: RenderPhoneInputProps) => {
  return (
    <TextField
      error={error}
      variant={variant}
      label={label}
      helperText={helperText}
      FormHelperTextProps={{
        sx: {
          fontSize: '0.75rem',
          marginInline: 1.5,
        },
      }}
      {...field}
      {...textFieldProps}
      value={(field.value as string) ?? value}
      placeholder={PHONE_FIELD_PLACEHOLDER}
      InputProps={{
        // MUI doesn't support `disableUnderline` for `outlined` input variant ¯\_(ツ)_/¯
        ...(!textFieldProps?.variant || textFieldProps?.variant !== 'outlined'
          ? { disableUnderline: true }
          : {}),
        ...textFieldProps?.InputProps,
        inputComponent: TextMaskPhone,
        type: 'tel',
      }}
    />
  );
};

export const FormPhoneInput = (props: FormPhoneInputProps) => {
  const {
    name,
    label,
    value,
    required,
    pattern,
    minLength,
    maxLength,
    textFieldProps,
    variant = 'standard',
    showsIf = [],
    debounced,
    helperText,
    ...rest
  } = props;
  const { control, formState } = useFormContext();
  const errorMsg = formState.errors[name]?.message;
  const hasErrorMsg = typeof errorMsg === 'string';

  const shouldShow = useWatchConditions({
    control,
    showsIf,
  });

  if (!shouldShow) {
    return null;
  }

  return (
    <FormControl variant={variant} {...rest} error={hasErrorMsg}>
      <Controller
        name={name}
        control={control}
        rules={{ required, pattern, minLength, maxLength }}
        render={({ field }) => (
          <RenderPhoneInput
            label={label}
            field={field}
            textFieldProps={textFieldProps}
            variant={variant}
            error={hasErrorMsg}
            value={value}
            debounced={debounced}
            helperText={helperText}
          />
        )}
      />
    </FormControl>
  );
};
